import dayjs from 'dayjs';

const colorCache = new Map(); // cache for colors to avoid re-calculating them

// Helper function to format date
export const formatDate = date => {
  const messageDate = dayjs(date);
  if (!messageDate.isValid()) return '';

  const now = dayjs();
  if (now.isSame(messageDate, 'day')) {
    return `Heute ${messageDate.format('HH:mm')}`;
  }

  if (now.subtract(1, 'day').isSame(messageDate, 'day')) {
    return `Gestern ${messageDate.format('HH:mm')}`;
  }

  const includeYear = now.year() !== messageDate.year();
  const datePart = messageDate.format(includeYear ? 'DD.MM.YYYY' : 'DD.MM.');
  return `${datePart} ${messageDate.format('HH:mm')}`;
};

// Helper function to generate consistent colors from strings
export const stringToColor = (str, alpha = 1) => {
  // Check cache first
  const cacheKey = `${str}-${alpha}`;
  if (colorCache.has(cacheKey)) {
    return colorCache.get(cacheKey);
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  // Reduced saturation to 45% and adjusted lightness to 45% for more muted colors
  const color = `hsla(${hue}, 45%, 45%, ${alpha})`;

  // Store in cache
  colorCache.set(cacheKey, color);
  return color;
};

// Format duration in milliseconds as German "x Stunden und y Minuten".
export const formatDurationDe = totalMs => {
  const ms = Math.max(0, Number(totalMs || 0));
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`);
  if (minutes > 0 || hours === 0) parts.push(`${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`);
  return parts.join(' und ');
};

// Compact: e.g. 1h 45 min, 2h, 30 min
export const formatDurationCompact = totalMs => {
  const ms = Math.max(0, Number(totalMs || 0));
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes} min`;
  if (hours > 0) return `${hours}h`;
  return `${minutes} min`;
};

// Format duration object with recommendedDurationUnit property
// e.g. duration object: { durationMS: 3600000, recommendedDurationUnit: 'h' }
export const formatDurationLabel = duration => {
  if (!duration) {
    return '';
  }

  if (duration.recommendedDurationUnit === 'm') {
    const minutes = Math.round(duration.durationMS / 60000);
    return `${minutes} min`;
  }

  if (duration.recommendedDurationUnit === 'h') {
    const hours = duration.durationMS / 3600000;
    return `${hours}h`;
  }

  return `${duration.durationMS / 3600000} ${duration.recommendedDurationUnit}`;
};

export const currencyFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

export const percentFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const decimalFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
